import { Button } from "@/components/ui/baseUi/button"
import { Checkbox } from "@/components/ui/baseUi/checkbox"
import { Text } from "@/components/ui/baseUi/text"
import { Textarea } from "@/components/ui/baseUi/textarea"
import {
  useCompletedTasks,
  useCreateTeamTask,
  useDeleteTeamTask,
  useTeamTasks,
  useToggleTeamTask,
  useUpdateTeamTask,
} from "@/modules/boxDashboard/modules/TeamTask/hooks/useTeamTasks"
import { createTeamTasksModel } from "@/modules/boxDashboard/modules/TeamTask/models/teamTasksModel"
import { cn } from "@/modules/lib/utils"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { Archive, Check, Edit2, Pencil, Plus, Trash2, X } from "lucide-react"
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react"

type TeamTasksBlockProps = {
  className?: string
} & React.ComponentProps<"div">
export function TeamTasksBlock({ className, ...props }: TeamTasksBlockProps) {
  const [newTaskContent, setNewTaskContent] = useState("")
  const [isAdding, setIsAdding] = useState(false)
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null)
  const [editingContent, setEditingContent] = useState("")
  const [hoveredTaskId, setHoveredTaskId] = useState<string | null>(null)
  const [showCompleted, setShowCompleted] = useState(false)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const canEditDashboard = useBoxStaffPermission(BoxStaffPermissions.EDIT_DASHBOARD)

  const {
    tasks: activeTasks,
    hasNextPage: hasNextPageActive,
    fetchNextPage: fetchNextPageActive,
    isFetchingNextPage: isFetchingNextPageActive,
  } = useTeamTasks()
  const {
    tasks: completedTasks,
    hasNextPage: hasNextPageCompleted,
    fetchNextPage: fetchNextPageCompleted,
    isFetchingNextPage: isFetchingNextPageCompleted,
  } = useCompletedTasks()
  const toggleTask = useToggleTeamTask()
  const createTask = useCreateTeamTask()
  const updateTask = useUpdateTeamTask()
  const deleteTask = useDeleteTeamTask()

  const hasNextPage = showCompleted ? hasNextPageCompleted : hasNextPageActive
  const fetchNextPage = showCompleted ? fetchNextPageCompleted : fetchNextPageActive
  const isFetchingNextPage = showCompleted ? isFetchingNextPageCompleted : isFetchingNextPageActive

  const model = useMemo(() => {
    return createTeamTasksModel({
      activeTasks,
      completedTasks,
      showCompleted,
      canEditDashboard,
    })
  }, [activeTasks, completedTasks, showCompleted, canEditDashboard])

  const handleAddTask = async () => {
    if (!canEditDashboard) return
    if (newTaskContent.trim().length < 3) return

    try {
      await createTask.mutateAsync({ task: newTaskContent.trim() })
      setNewTaskContent("")
      setIsAdding(false)
    } catch (error) {
      console.error("Failed to create task:", error)
    }
  }

  const handleToggleTask = async (taskId: string, isCompleted: boolean) => {
    if (!canEditDashboard) return
    try {
      await toggleTask.mutateAsync({ id: taskId, isCompleted })
    } catch (error) {
      console.error("Failed to toggle task:", error)
    }
  }

  const handleStartEdit = (taskId: string, content: string) => {
    if (!canEditDashboard) return
    setEditingTaskId(taskId)
    setEditingContent(content)
  }

  const handleUpdateTask = async (taskId: string) => {
    if (!canEditDashboard) return
    if (editingContent.trim().length < 3) return

    try {
      await updateTask.mutateAsync({ id: taskId, data: { task: editingContent.trim() } })
      setEditingTaskId(null)
      setEditingContent("")
    } catch (error) {
      console.error("Failed to update task:", error)
    }
  }

  const handleDeleteTask = async (taskId: string) => {
    if (!canEditDashboard) return
    try {
      await deleteTask.mutateAsync(taskId)
      setEditingTaskId(null)
      setEditingContent("")
    } catch (error) {
      console.error("Failed to delete task:", error)
    }
  }

  const handleCancelEdit = () => {
    setEditingTaskId(null)
    setEditingContent("")
  }

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    })

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)

      // Check if the element is already visible on mount (for large screens)
      const rect = currentRef.getBoundingClientRect()
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0
      if (isVisible && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    }

    return () => {
      observer.disconnect()
    }
  }, [handleIntersection, hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div
      className={cn("flex h-140 flex-col gap-2.5 overflow-clip rounded-3xl bg-surface-foreground pt-6 pb-0", className)}
      {...props}
    >
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between px-6 pr-4">
        <Text fontStyle="HS" className="text-textDefault-heading">
          {word(model.title)}
        </Text>
        {(model.hasCompletedTasks || showCompleted) && (
          <Button
            variant="secondary"
            size="iconLg"
            className="bg-surface-background"
            onClick={() => {
              startTransition(() => {
                setShowCompleted(!showCompleted)
              })
            }}
          >
            {showCompleted ? <X className="size-6" /> : <Archive className="size-6" />}
          </Button>
        )}
      </div>

      {/* Content area */}
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Task list */}
        <div
          className="scrollbar-textOnSurface-muted flex min-h-0 flex-1 flex-col overflow-y-auto pb-2"
          id="teamTasksScrollableDiv"
        >
          {model.hasTasks && (
            <div className="flex flex-col">
              {model.tasks.map((task) => {
                const isEditing = editingTaskId === task.id
                const isHovered = hoveredTaskId === task.id

                if (isEditing) {
                  return (
                    <div
                      key={task.id}
                      className="flex items-start justify-between bg-surface-background-float py-1 pr-1 pl-6"
                    >
                      <div className="flex flex-1 items-start gap-1 px-0 py-0.5">
                        <div className="flex items-start gap-2 p-0.5">
                          <div className="h-5 w-5 rounded border-2 border-interactive-border-neutral-default" />
                        </div>
                        <Textarea
                          value={editingContent}
                          onChange={(e) => {
                            setEditingContent(e.target.value)
                          }}
                          className="min-h-0 flex-1 resize-none rounded-none border-none bg-transparent p-0 text-base leading-normal break-all text-textOnSurface-default focus-visible:ring-0"
                          maxLength={240}
                          autoFocus
                          rows={1}
                          style={{ height: "auto" }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault()
                              void handleUpdateTask(task.id)
                            } else if (e.key === "Escape") {
                              handleCancelEdit()
                            }
                          }}
                        />
                      </div>
                      <div className="flex flex-col items-center gap-2 self-stretch pr-3">
                        <Button
                          size="iconMd"
                          variant="primary"
                          onClick={() => void handleUpdateTask(task.id)}
                          disabled={!canEditDashboard || editingContent.trim().length < 3 || updateTask.isPending}
                        >
                          <Check className="h-5 w-5" />
                        </Button>
                        <Button
                          size="iconMd"
                          variant="secondary"
                          onClick={() => void handleDeleteTask(task.id)}
                          disabled={!canEditDashboard || deleteTask.isPending}
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  )
                }

                return (
                  <div
                    key={task.id}
                    className={cn(
                      "flex items-start justify-between py-1 pr-1 pl-6",
                      model.canEditTask && isHovered && "bg-surface-background-float",
                    )}
                    onMouseEnter={() => {
                      if (model.canEditTask) {
                        setHoveredTaskId(task.id)
                      }
                    }}
                    onMouseLeave={() => {
                      if (model.canEditTask) {
                        setHoveredTaskId(null)
                      }
                    }}
                  >
                    <div className="flex flex-1 flex-col items-start justify-center pl-0">
                      <div className="flex w-full items-start gap-1 px-0 py-0.5">
                        <div className="flex items-start gap-2 p-0.5">
                          <Checkbox
                            checked={task.isDone}
                            disabled={!model.canToggleTask}
                            onCheckedChange={() => {
                              if (!canEditDashboard) {
                                return
                              }
                              if (toggleTask.isPending) {
                                return
                              }
                              void handleToggleTask(task.id, task.isDone)
                            }}
                            className="h-5 w-5 rounded border-2 border-interactive-border-neutral-default"
                          />
                        </div>
                        <div className="flex flex-1 flex-col items-start">
                          <Text
                            fontStyle="BM"
                            className={cn(
                              "text-left break-all text-textOnSurface-subtle",
                              task.isDone && "line-through",
                            )}
                          >
                            {task.content}
                          </Text>
                          {task.isDone ? (
                            <div className="flex flex-col gap-0.5">
                              {task.completedAt && task.completedBy && (
                                <div className="flex items-center gap-0.5">
                                  <Check className="h-3 w-3 shrink-0 text-textOnSurface-muted" />
                                  <Text fontStyle="BS" className="truncate text-textOnSurface-muted">
                                    {task.completedAt} by {task.completedBy.fullName}
                                  </Text>
                                </div>
                              )}
                              <div className="flex items-center gap-0.5">
                                <Pencil className="h-3 w-3 shrink-0 text-textOnSurface-disabled" />
                                <Text fontStyle="BS" className="truncate text-textOnSurface-disabled">
                                  {task.createdAt} by {task.createdBy.fullName}
                                </Text>
                              </div>
                            </div>
                          ) : (
                            <Text fontStyle="BS" className="text-textOnSurface-muted">
                              {task.createdAt} by {task.createdBy.fullName}
                            </Text>
                          )}
                        </div>
                      </div>
                    </div>
                    {model.canEditTask && isHovered && (
                      <div className="flex items-center justify-end pr-3">
                        <Button
                          size="iconMd"
                          variant="secondary"
                          onClick={() => {
                            handleStartEdit(task.id, task.content)
                          }}
                          disabled={!canEditDashboard}
                        >
                          <Edit2 className="h-5 w-5" />
                        </Button>
                      </div>
                    )}
                  </div>
                )
              })}
              {(hasNextPage || isFetchingNextPage) && (
                <div ref={loadMoreRef} className="flex w-full items-center justify-center">
                  <Text fontStyle="BM">{word("global.loading")}</Text>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add task section */}
        {model.canAddTask && isAdding ? (
          <div className="shrink-0 border-t border-interactive-border-neutral-default bg-surface-background-float px-1 py-2">
            <div className="flex min-h-12 items-center gap-2 px-4">
              <div className="flex items-center gap-2.5 px-0 py-1">
                <div className="h-4 w-4 rounded border-2 border-interactive-border-neutral-default" />
              </div>
              <Textarea
                value={newTaskContent}
                onChange={(e) => {
                  setNewTaskContent(e.target.value)
                }}
                placeholder={word("boxDashboard.teamTasks.addTask")}
                className="min-h-0 flex-1 resize-none rounded-none border-none bg-transparent p-0 text-base leading-normal break-all text-textOnSurface-default placeholder:text-textOnSurface-subtle focus-visible:ring-0"
                maxLength={240}
                autoFocus
                rows={1}
                wrap={"hard"}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault()
                    void handleAddTask()
                  } else if (e.key === "Escape") {
                    setIsAdding(false)
                    setNewTaskContent("")
                  }
                }}
                onBlur={() => {
                  if (newTaskContent.trim().length === 0) {
                    setIsAdding(false)
                  }
                }}
              />
              <Button
                size="iconLg"
                variant={"primary"}
                onClick={(e) => {
                  e.preventDefault()
                  void handleAddTask()
                }}
                onMouseDown={(e) => {
                  e.preventDefault()
                }}
                disabled={!canEditDashboard || newTaskContent.trim().length < 3 || createTask.isPending}
              >
                <Check className="size-6" />
              </Button>
            </div>
          </div>
        ) : (
          model.canAddTask && (
            <button
              className={cn(
                "flex shrink-0 cursor-text rounded-none border-t border-interactive-border-neutral-default bg-surface-background-float px-1 py-2 hover:bg-surface-foreground",
                model.hasTasks ? "" : "h-full flex-col items-center justify-center",
              )}
              onClick={() => {
                if (!canEditDashboard) {
                  return
                }
                setIsAdding(true)
              }}
            >
              <div className={cn("flex h-12 items-center gap-0.5 px-4", model.hasTasks ? "w-full" : "")}>
                <div className="flex items-center gap-2.5 px-0 py-1">
                  <Plus className="h-4 w-4 text-textOnSurface-subtle" />
                </div>
                <Text fontStyle="BM" className="text-textOnSurface-subtle">
                  {word("boxDashboard.teamTasks.addTask")}
                </Text>
              </div>
            </button>
          )
        )}
      </div>
    </div>
  )
}
